import { useEffect, useState, useCallback } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Plus, PencilSimple, Trash, ShieldCheck, CheckCircle, XCircle } from '@phosphor-icons/react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Switch } from '@/components/ui/switch';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '@/components/ui/dialog';
import useDirectory from '@/hooks/useDirectory';
import { shizaiApi } from '@/pages/shizai/shizaiApi';
import ShizaiApprovalsAuditCard from './ShizaiApprovalsAuditCard';
import ShizaiApprovalsSimulatorCard from './ShizaiApprovalsSimulatorCard';

const API_ROOT = `${process.env.REACT_APP_BACKEND_URL || ''}/api`;
const NONE = '__none__';
const PRIO_LABEL = { low: '低', normal: '普通', high: '高', urgent: '緊急' };

const emptyForm = () => ({
  name: '', department_id: '', min_amount: 0, priority_min: 'normal',
  approver_id: '', approver_email: '', active: true, sort_order: 100,
  idle_days: 2, fallback_approver_id: '', fallback_approver_email: '',
  order_scope: 'any',  // 'any' | 'type' | 'code'
  order_type: '',       // '通常受注' | '社内受注' (when scope=type)
  order_category_code: '', // exact code (when scope=code)
});

/** Admin settings for Shizai PO approval rules. Rules are evaluated top-to-bottom
 *  by sort_order (lower = higher priority) — first match wins. */
export default function ShizaiApprovalsSettings() {
  const [rules, setRules] = useState([]);
  const [loading, setLoading] = useState(false);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(emptyForm());
  const [busy, setBusy] = useState(false);
  const [orderCategories, setOrderCategories] = useState([]);
  const { employees, departments } = useDirectory();

  const load = useCallback(() => {
    setLoading(true);
    axios.get(`${API_ROOT}/shizai/approval-rules`)
      .then((r) => setRules(r.data || []))
      .catch((err) => { console.error('[approvals] load failed', err); toast.error('データの取得に失敗しました'); })
      .finally(() => setLoading(false));
  }, []);
  useEffect(() => { load(); }, [load]);
  useEffect(() => {
    shizaiApi.orderCategories()
      .then((rows) => setOrderCategories((rows || []).filter((c) => c.active !== false)))
      .catch((err) => console.error('[approvals] order-categories failed', err));
  }, []);

  const openNew = () => { setEditing(null); setForm(emptyForm()); setDialogOpen(true); };
  const openEdit = (r) => {
    setEditing(r);
    let scope = 'any';
    if (r.order_category_code) scope = 'code';
    else if (r.order_type) scope = 'type';
    setForm({
      name: r.name, department_id: r.department_id || '',
      min_amount: r.min_amount || 0, priority_min: r.priority_min || 'normal',
      approver_id: r.approver_id || '', approver_email: r.approver_email || '',
      active: !!r.active, sort_order: r.sort_order || 100,
      idle_days: r.idle_days ?? 2,
      fallback_approver_id: r.fallback_approver_id || '',
      fallback_approver_email: r.fallback_approver_email || '',
      order_scope: scope,
      order_type: r.order_type || '',
      order_category_code: r.order_category_code || '',
    });
    setDialogOpen(true);
  };

  const save = async () => {
    if (!form.name.trim()) { toast.error('ルール名を入力してください'); return; }
    if (!form.approver_email.trim()) { toast.error('承認者のメールアドレスを入力してください'); return; }
    if (form.order_scope === 'type' && !form.order_type) {
      toast.error('タイプを選択してください'); return;
    }
    if (form.order_scope === 'code' && !form.order_category_code) {
      toast.error('分類を選択してください'); return;
    }
    setBusy(true);
    try {
      const body = {
        ...form,
        department_id: form.department_id || null,
        min_amount: Number(form.min_amount) || 0,
        sort_order: Number(form.sort_order) || 100,
        idle_days: Math.max(0, Math.min(30, Number(form.idle_days) || 2)),
        fallback_approver_id: form.fallback_approver_id || null,
        fallback_approver_email: form.fallback_approver_email || null,
        // v3: 受注分類 dimension — only one of the two set at a time
        order_type: form.order_scope === 'type' ? form.order_type : null,
        order_category_code: form.order_scope === 'code' ? form.order_category_code : null,
      };
      // Frontend-only field; not part of API contract
      delete body.order_scope;
      if (editing) {
        await axios.put(`${API_ROOT}/shizai/approval-rules/${editing.id}`, body);
        toast.success('ルールを更新しました');
      } else {
        await axios.post(`${API_ROOT}/shizai/approval-rules`, body);
        toast.success('ルールを作成しました');
      }
      setDialogOpen(false);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || '保存に失敗しました');
    } finally { setBusy(false); }
  };

  const del = async (r) => {
    if (!window.confirm(`${r.name} を削除しますか？`)) return;
    try {
      await axios.delete(`${API_ROOT}/shizai/approval-rules/${r.id}`);
      toast.success('削除しました');
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || '削除に失敗しました');
    }
  };

  const toggleActive = async (r) => {
    try {
      await axios.put(`${API_ROOT}/shizai/approval-rules/${r.id}`, { ...r, active: !r.active });
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || '状態変更に失敗しました');
    }
  };

  return (
    <div className="space-y-5 page-enter" data-testid="shizai-approval-rules-settings">
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div className="flex items-center gap-2">
          <ShieldCheck size={22} weight="duotone" className="text-emerald-500" />
          <h1 className="text-2xl font-bold tracking-tight text-zinc-900">発注承認ルール</h1>
          <span className="text-xs text-zinc-500 tabular-nums">({rules.length}件)</span>
        </div>
        <Button onClick={openNew} data-testid="ar-add" className="h-10 bg-teal-700 hover:bg-teal-800">
          <Plus size={14} className="mr-1" weight="bold" />ルール追加
        </Button>
      </div>

      <div className="text-xs text-zinc-500 bg-emerald-50 border border-emerald-200 rounded-lg p-3">
        💡 ルールは「並び順」の小さい方から順に評価され、<b>最初に一致したルール</b>が適用されます。<br />
        <b>発注作成時に自動で承認依頼が送信されます</b>。金額・部署・優先度・<b>分類</b>を条件に、担当者にメールで承認リンクが届きます。
      </div>

      {loading ? (
        <div className="p-8 text-center text-sm text-zinc-400">読み込み中...</div>
      ) : rules.length === 0 ? (
        <div className="bg-white rounded-xl border border-zinc-200 shadow-sm p-12 text-center" data-testid="ar-empty">
          <ShieldCheck size={40} weight="duotone" className="text-zinc-300 mx-auto mb-2" />
          <p className="text-sm text-zinc-500">承認ルールが設定されていません</p>
          <p className="text-xs text-zinc-400 mt-1">ルールを追加すると、条件に一致する発注に自動で承認フローが起動します</p>
        </div>
      ) : (
        <div className="bg-white rounded-xl border border-zinc-200 shadow-sm overflow-hidden">
          <table className="w-full text-sm">
            <thead className="bg-zinc-50 text-xs text-zinc-500">
              <tr>
                <th className="text-left px-3 py-2 font-semibold">#</th>
                <th className="text-left px-3 py-2 font-semibold">ルール名</th>
                <th className="text-left px-3 py-2 font-semibold">部署</th>
                <th className="text-right px-3 py-2 font-semibold">金額(以上)</th>
                <th className="text-left px-3 py-2 font-semibold">優先度(以上)</th>
                <th className="text-left px-3 py-2 font-semibold">分類</th>
                <th className="text-left px-3 py-2 font-semibold">承認者</th>
                <th className="text-left px-3 py-2 font-semibold">エスカレート</th>
                <th className="text-center px-3 py-2 font-semibold">有効</th>
                <th className="text-right px-3 py-2 font-semibold">操作</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-zinc-200">
              {rules.map((r) => {
                const dept = departments.find((d) => d.id === r.department_id);
                return (
                  <tr key={r.id} data-testid={`ar-row-${r.id}`} className={r.active ? '' : 'bg-zinc-50/60 opacity-60'}>
                    <td className="px-3 py-2 tabular-nums">{r.sort_order}</td>
                    <td className="px-3 py-2 font-medium text-zinc-800">{r.name}</td>
                    <td className="px-3 py-2 text-zinc-600">{dept?.name || '全部署'}</td>
                    <td className="px-3 py-2 text-right tabular-nums">¥{Number(r.min_amount || 0).toLocaleString()}</td>
                    <td className="px-3 py-2">
                      <Badge variant="outline" className="text-[10px]">{PRIO_LABEL[r.priority_min] || r.priority_min}</Badge>
                    </td>
                    <td className="px-3 py-2 text-xs">
                      {r.order_category_code ? (
                        <span className="inline-flex items-center gap-1">
                          <span className="font-mono text-emerald-700">{r.order_category_code}</span>
                          <span className="text-zinc-500 truncate max-w-[110px]">
                            {orderCategories.find((c) => c.code === r.order_category_code)?.name || ''}
                          </span>
                        </span>
                      ) : r.order_type ? (
                        <Badge variant="outline" className="text-[10px] bg-emerald-50 text-emerald-700 border-emerald-200">{r.order_type}</Badge>
                      ) : (
                        <span className="text-zinc-300">全分類</span>
                      )}
                    </td>
                    <td className="px-3 py-2 text-zinc-600 text-xs">{r.approver_email}</td>
                    <td className="px-3 py-2 text-xs">
                      {r.fallback_approver_email ? (
                        <div className="flex flex-col leading-tight">
                          <span className="text-orange-600 font-mono">{r.idle_days ?? 2}日後 →</span>
                          <span className="text-zinc-600 truncate max-w-[180px]">{r.fallback_approver_email}</span>
                        </div>
                      ) : (
                        <span className="text-zinc-300">—</span>
                      )}
                    </td>
                    <td className="px-3 py-2 text-center">
                      <Switch checked={!!r.active} onCheckedChange={() => toggleActive(r)}
                        data-testid={`ar-toggle-${r.id}`} />
                    </td>
                    <td className="px-3 py-2 text-right">
                      <Button size="sm" variant="ghost" onClick={() => openEdit(r)}
                        data-testid={`ar-edit-${r.id}`} className="h-8 px-2">
                        <PencilSimple size={14} />
                      </Button>
                      <Button size="sm" variant="ghost" onClick={() => del(r)}
                        data-testid={`ar-del-${r.id}`} className="h-8 px-2 text-red-600 hover:bg-red-50">
                        <Trash size={14} />
                      </Button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {/* iter_143d · P2 audit trail — live view of every history event.
          Auto-refreshes every 60 s so auto-escalations show up within
          minutes of the server-side sweep firing. */}
      <ShizaiApprovalsAuditCard />

      {/* iter_143e · P2/v4 approval simulator — dry-run a hypothetical PO
          against the current rule set to preview which rule would fire. */}
      <ShizaiApprovalsSimulatorCard />

      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="max-w-lg" data-testid="ar-dialog">
          <DialogHeader>
            <DialogTitle>{editing ? '承認ルールを編集' : '承認ルールを追加'}</DialogTitle>
            <DialogDescription>
              金額・部署・優先度・分類 の条件を設定します。すべての条件を満たす発注に対し、このルールの承認者へ自動で承認依頼が送信されます。
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div>
              <Label className="text-xs font-semibold text-zinc-600">ルール名<span className="text-red-500 ml-0.5">*</span></Label>
              <Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })}
                placeholder="高額購入承認 / 部長承認 / 経理レビュー"
                data-testid="ar-form-name"
                className="h-10 mt-1" />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-xs font-semibold text-zinc-600">部署</Label>
                <Select value={form.department_id || NONE}
                  onValueChange={(v) => setForm({ ...form, department_id: v === NONE ? '' : v })}>
                  <SelectTrigger data-testid="ar-form-dept" className="h-10 mt-1"><SelectValue placeholder="全部署" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value={NONE}>全部署（ワイルドカード）</SelectItem>
                    {departments.map((d) => <SelectItem key={d.id} value={d.id}>{d.name}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-xs font-semibold text-zinc-600">並び順</Label>
                <Input type="number" value={form.sort_order}
                  onChange={(e) => setForm({ ...form, sort_order: e.target.value })}
                  data-testid="ar-form-sort"
                  className="h-10 mt-1" />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-xs font-semibold text-zinc-600">金額（円）以上</Label>
                <Input type="number" value={form.min_amount}
                  onChange={(e) => setForm({ ...form, min_amount: e.target.value })}
                  data-testid="ar-form-amount"
                  className="h-10 mt-1 tabular-nums" />
              </div>
              <div>
                <Label className="text-xs font-semibold text-zinc-600">優先度以上</Label>
                <Select value={form.priority_min} onValueChange={(v) => setForm({ ...form, priority_min: v })}>
                  <SelectTrigger data-testid="ar-form-priority" className="h-10 mt-1"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="low">低</SelectItem>
                    <SelectItem value="normal">普通</SelectItem>
                    <SelectItem value="high">高</SelectItem>
                    <SelectItem value="urgent">緊急</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>

            {/* 受注分類 dimension */}
            <div className="rounded-lg border border-zinc-200 p-3 bg-zinc-50/40">
              <Label className="text-xs font-semibold text-zinc-700">分類</Label>
              <div className="mt-1.5 inline-flex rounded-md border border-zinc-200 overflow-hidden bg-white" data-testid="ar-form-order-scope">
                {[
                  { v: 'any',  label: 'すべて' },
                  { v: 'type', label: 'タイプ' },
                  { v: 'code', label: '分類コード' },
                ].map((o) => (
                  <button key={o.v} type="button"
                    onClick={() => setForm({ ...form, order_scope: o.v })}
                    data-testid={`ar-form-order-scope-${o.v}`}
                    className={`px-3 py-1.5 text-xs font-medium transition-colors ${form.order_scope === o.v ? 'bg-emerald-500 text-white' : 'text-zinc-600 hover:bg-zinc-50'}`}>
                    {o.label}
                  </button>
                ))}
              </div>
              {form.order_scope === 'type' && (
                <div className="mt-2">
                  <Select value={form.order_type || NONE} onValueChange={(v) => setForm({ ...form, order_type: v === NONE ? '' : v })}>
                    <SelectTrigger data-testid="ar-form-order-type" className="h-10"><SelectValue placeholder="タイプを選択（通常受注 / 社内受注）" /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value={NONE}>選択なし</SelectItem>
                      <SelectItem value="通常受注">通常受注（外部顧客）</SelectItem>
                      <SelectItem value="社内受注">社内受注</SelectItem>
                    </SelectContent>
                  </Select>
                  <p className="text-[10px] text-zinc-400 mt-0.5">選んだタイプの全カテゴリに適用されます</p>
                </div>
              )}
              {form.order_scope === 'code' && (
                <div className="mt-2">
                  <Select value={form.order_category_code || NONE}
                    onValueChange={(v) => setForm({ ...form, order_category_code: v === NONE ? '' : v })}>
                    <SelectTrigger data-testid="ar-form-order-code" className="h-10"><SelectValue placeholder="分類コードを選択" /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value={NONE}>選択なし</SelectItem>
                      {orderCategories.map((c) => (
                        <SelectItem key={c.code} value={c.code}>
                          {c.code} — {c.name}{c.symbol ? ` (${c.symbol})` : ''} / {c.order_type}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <p className="text-[10px] text-zinc-400 mt-0.5">選んだ分類コードにのみ適用されます</p>
                </div>
              )}
              {form.order_scope === 'any' && (
                <p className="text-[10px] text-zinc-400 mt-1.5">すべての分類に適用（デフォルト）</p>
              )}
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-xs font-semibold text-zinc-600">承認者<span className="text-red-500 ml-0.5">*</span></Label>
                <Select value={form.approver_id || NONE}
                  onValueChange={(v) => {
                    if (v === NONE) { setForm({ ...form, approver_id: '', approver_email: '' }); return; }
                    const emp = employees.find((e) => e.id === v);
                    setForm({ ...form, approver_id: v, approver_email: emp?.email || form.approver_email });
                  }}>
                  <SelectTrigger data-testid="ar-form-approver" className="h-10 mt-1"><SelectValue placeholder="選択" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value={NONE}>選択なし</SelectItem>
                    {employees.filter((e) => e.email).map((e) => (
                      <SelectItem key={e.id} value={e.id}>{e.name}（{e.email}）</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-xs font-semibold text-zinc-600">承認者メール<span className="text-red-500 ml-0.5">*</span></Label>
                <Input value={form.approver_email}
                  onChange={(e) => setForm({ ...form, approver_email: e.target.value })}
                  data-testid="ar-form-email"
                  className="h-10 mt-1" />
              </div>
            </div>
            <label className="flex items-center gap-2 cursor-pointer">
              <Switch checked={form.active} onCheckedChange={(v) => setForm({ ...form, active: !!v })}
                data-testid="ar-form-active" />
              <span className="text-sm text-zinc-700">有効</span>
            </label>

            {/* v2 — Escalation settings */}
            <div className="pt-3 mt-1 border-t border-zinc-200">
              <div className="text-xs font-semibold text-zinc-700 mb-2 flex items-center gap-1.5">
                <span className="inline-block w-1.5 h-1.5 rounded-full bg-orange-400" />
                エスカレーション（v2）
              </div>
              <div className="grid grid-cols-3 gap-3">
                <div>
                  <Label className="text-xs font-semibold text-zinc-600">滞留日数</Label>
                  <Input type="number" min={0} max={30} value={form.idle_days}
                    onChange={(e) => setForm({ ...form, idle_days: e.target.value })}
                    data-testid="ar-form-idle-days"
                    className="h-10 mt-1 tabular-nums" />
                  <p className="text-[10px] text-zinc-400 mt-0.5">承認されずに経過する日数（既定 2 日）</p>
                </div>
                <div className="col-span-2">
                  <Label className="text-xs font-semibold text-zinc-600">代理承認者</Label>
                  <Select value={form.fallback_approver_id || NONE}
                    onValueChange={(v) => {
                      if (v === NONE) { setForm({ ...form, fallback_approver_id: '', fallback_approver_email: '' }); return; }
                      const emp = employees.find((e) => e.id === v);
                      setForm({ ...form, fallback_approver_id: v, fallback_approver_email: emp?.email || form.fallback_approver_email });
                    }}>
                    <SelectTrigger data-testid="ar-form-fallback" className="h-10 mt-1"><SelectValue placeholder="未設定（エスカレートしない）" /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value={NONE}>未設定</SelectItem>
                      {employees.filter((e) => e.email).map((e) => (
                        <SelectItem key={e.id} value={e.id}>{e.name}（{e.email}）</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>
              <Input value={form.fallback_approver_email}
                onChange={(e) => setForm({ ...form, fallback_approver_email: e.target.value })}
                placeholder="fallback@example.com（メール直接入力も可）"
                data-testid="ar-form-fallback-email"
                className="h-10 mt-2" />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDialogOpen(false)}>キャンセル</Button>
            <Button onClick={save} disabled={busy}
              data-testid="ar-form-save"
              className="bg-teal-700 hover:bg-teal-800">
              {busy ? '保存中…' : '保存'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
